import React, { useCallback, useEffect, useState, useOptimistic } from 'react';
import { useNavigate } from 'react-router';
import { Dialog, Loader, useNotification } from '@dynamon-as/ui';
import {
    selectError,
    useRequestStore,
    useRequests,
    RequestStatus,
} from '@features/requests';
import type { Request } from '@features/requests/types/requestTypes';
import { useEmployeeId } from '@shared/hooks';
import { RequestList } from '@features/requests/components';
import { useI18n, useRequestTranslations } from '@shared/i18n';

import styles from './RequestsList.module.css';

/**
 * Requests List Page
 * Header, loading state and the grouped request list.
 * Submit is confirmed via a dialog before execution.
 */
export default function RequestsList(): React.ReactElement {
    const navigate = useNavigate();
    const { notify } = useNotification();
    const { t } = useI18n();
    const { resubmitDialog } = useRequestTranslations();

    const { orgEmployeeId, error: employeeError } = useEmployeeId();
    const {
        requests: baseRequests,
        isLoading,
        error: requestsError,
        submitRequest,
    } = useRequests(orgEmployeeId);

    // Use optimistic updates for request status
    const [optimisticRequests, updateRequestOptimistic] = useOptimistic(
        baseRequests,
        (currentRequests: Request[], requestId: string): Request[] => {
            return currentRequests.map((req: Request): Request =>
                req.id === requestId
                    ? { ...req, status: RequestStatus.SUBMITTED }
                    : req,
            );
        },
    );

    const totalCount = optimisticRequests.length;

    const storeError = useRequestStore(selectError);

    const [pendingSubmitId, setPendingSubmitId] = useState<string | null>(null);
    const [isActionLoading, setIsActionLoading] = useState(false);

    // Resolve the pending request to adapt the dialog (resubmission case)
    const pendingRequest = pendingSubmitId
        ? optimisticRequests.find((r): boolean => r.id === pendingSubmitId)
        : null;
    const isResubmission = pendingRequest?.status === RequestStatus.REJECTED;

    useEffect((): void => {
        if (employeeError) {
            notify.error(employeeError);
        }
    }, [employeeError, notify]);

    useEffect((): void => {
        if (requestsError) {
            notify.error(requestsError);
        }
    }, [requestsError, notify]);

    useEffect((): void => {
        if (storeError) {
            notify.error(storeError);
            const clearError = useRequestStore.getState().clearError;
            clearError();
        }
    }, [storeError, notify]);

    const handleViewDetails = useCallback(
        (requestId: string): void => {
            const selectRequest = useRequestStore.getState().selectRequest;
            selectRequest(requestId);
            navigate(`/requests/${requestId}`);
        },
        [navigate],
    );

    const handleEdit = useCallback(
        (requestId: string): void => {
            navigate(`/requests/${requestId}/edit`);
        },
        [navigate],
    );

    const handleSubmit = useCallback((requestId: string): void => {
        setPendingSubmitId(requestId);
    }, []);

    const handleCloseDialog = useCallback((): void => {
        if (!isActionLoading) {
            setPendingSubmitId(null);
        }
    }, [isActionLoading]);

    const handleConfirmSubmit = useCallback(async (): Promise<void> => {
        if (!pendingSubmitId) {
            return;
        }
        setIsActionLoading(true);

        // Update optimistically
        updateRequestOptimistic(pendingSubmitId);

        try {
            const result = await submitRequest(pendingSubmitId);
            if (result.success) {
                notify.success(t('requests.notifications.requestSent'), {
                    duration: 5000,
                });
            } else {
                notify.error(result.error ?? t('common.error'), {
                    duration: 5000,
                });
            }
        } catch {
            notify.error(t('common.error'), {
                duration: 5000,
            });
        } finally {
            setIsActionLoading(false);
            // Always close the dialog; the notification reports the outcome
            setPendingSubmitId(null);
        }
    }, [pendingSubmitId, submitRequest, notify, updateRequestOptimistic, t]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>
                    {t('requests.pageTitle', { count: totalCount })}
                </h1>
                <p className={styles.subtitle}>{t('requests.pageSubtitle')}</p>
            </div>

            <Loader
                isLoading={isLoading}
                variant="inline"
                label={t('requests.list.loading')}
            >
                <RequestList
                    requests={optimisticRequests}
                    onSelect={handleViewDetails}
                    onEdit={handleEdit}
                    onSubmit={handleSubmit}
                />
            </Loader>

            <Dialog
                open={pendingSubmitId !== null}
                onClose={handleCloseDialog}
                title={
                    isResubmission
                        ? resubmitDialog.title
                        : t('requests.form.actions.submit')
                }
                confirmText={
                    isResubmission
                        ? resubmitDialog.confirm
                        : t('requests.form.actions.submit')
                }
                cancelText={t('common.cancel')}
                isLoading={isActionLoading}
                onConfirm={(): void => void handleConfirmSubmit()}
            >
                <p className={styles.dialogText}>
                    {isResubmission
                        ? resubmitDialog.text
                        : t('requests.details.resubmit', {
                              context: 'confirm',
                          })}
                </p>
            </Dialog>
        </div>
    );
}
